import React, { useCallback, useMemo, useRef } from 'react';
import _get from 'lodash/get';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import FormTemplateBuilderForm from 'components/FormTemplateBuilder/FormTemplateBuilderForm/FormTemplateBuilderForm';
import DocumentsModalTableMultiselect from 'components/DocumentsModalTableMultiselect/DocumentsModalTableMultiselect';
import TasksTableMultiselect from 'components/TasksTableMultiselect/TasksTableMultiselect';
import { FieldNames as TasksFilterFieldNames } from '@lib/components/TaskCompletions/TasksTableMultiselect/enums';
import useSelectedVersionOption from 'routes/FormTemplate/utils/useSelectedVersionOption';
import FormTemplateModals from 'routes/FormTemplate/FormTemplateModals';
import useSearchListQueries from 'routes/FormTemplate/utils/useSearchListQueries';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FormTemplateQuestionFieldsKindEnum,
  TaskScopeInputObject,
} from 'graphql-common';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import useLibModalManagement from '@lib/hooks/useModalManagement';
import { Option, Values } from '@lib/interfaces/form';
import FormTemplateFieldNames from '@lib/enums/fieldNames/formTemplateFieldNames';
import getFormTemplateFormFields from './utils/getFormTemplateFormFields';
import useModalManagement from './utils/useModalManagement';
import useActions from './utils/useActions';
import useDocumentationLinksPrefill from './utils/useDocumentationLinksPrefill';
import { FormTemplateMode } from './enums';

interface Props {
  viewMode: FormTemplateMode;
}

const SELECT_DOCUMENTS_MODAL = 'select-documents';
const SELECT_TASKS_MODAL = 'select-tasks';
// Every task here already uses this template, so a category filter is moot.
const TASKS_HIDDEN_FILTER_FIELDS = [TasksFilterFieldNames.FormCategories];

export default function FormTemplateBuilderFormContainer(props: Props) {
  const { viewMode } = props;
  const formRef = useRef<FormRef>(undefined);
  const { id = '', versionId = '' } = useParams();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const {
    getUserAccessByCompany,
    getUserAccessByComponent,
    isFullAdmin,
  }: ContextProps = useOutletContext();
  const isCategoriesViewOnly =
    getUserAccessByComponent(AccessComponentEnum.FormCategoriesManagement) ===
    AccessLevelEnum.Read;
  const isViewLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  // EDM-locked + non-Full-Admin: strip Document questions from the form
  // defaults so they're neither rendered nor submitted (RHF state is seeded
  // from this shape, and the submit payload is derived from RHF state).
  const hideDocumentQuestions = isViewLocked && !isFullAdmin;

  const initialDocumentationLinksRef = useRef<Option[]>([]);

  const useModalManagementResult = useModalManagement();

  // Documents / tasks modals
  const useSelectModalsResult = useLibModalManagement([
    SELECT_DOCUMENTS_MODAL,
    SELECT_TASKS_MODAL,
  ]);

  const openDocumentsModal = useCallback(() => {
    useSelectModalsResult.openModal(SELECT_DOCUMENTS_MODAL);
  }, [useSelectModalsResult]);

  const onCloseDocumentsModal = useCallback(() => {
    useSelectModalsResult.closeModal(SELECT_DOCUMENTS_MODAL);
  }, [useSelectModalsResult]);

  const onDocumentsSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        FormTemplateFieldNames.DocumentationLinks,
        values.length ? values.map((docId) => ({ value: docId })) : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const isDocumentsModalOpen = useSelectModalsResult.isModalOpened(
    SELECT_DOCUMENTS_MODAL,
  );

  const openTasksModal = useCallback(() => {
    useSelectModalsResult.openModal(SELECT_TASKS_MODAL);
  }, [useSelectModalsResult]);

  const onCloseTasksModal = useCallback(() => {
    useSelectModalsResult.closeModal(SELECT_TASKS_MODAL);
  }, [useSelectModalsResult]);

  // The picker yields recurring-chain group keys; `useActions` resolves them
  // back to task ids on submit.
  const onTasksSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        FormTemplateFieldNames.Tasks,
        values.length ? values.map((groupKey) => ({ value: groupKey })) : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  // Same scope the version mutations validate `taskIds` against.
  const tasksScope = useMemo<TaskScopeInputObject>(
    () => ({ custom: { linkedToFormTemplateGrouped: { templateId: id } } }),
    [id],
  );
  const {
    closeCategoryFormModal,
    isCategoryFormModalOpened,
    openCategoryFormModal,
  } = useModalManagementResult;

  const useActionsResult = useActions({
    useModalManagementResult,
    formRef,
    id,
    initialDocumentationLinksRef,
    isViewLocked,
    versionId,
    viewMode,
  });
  const {
    formCategoryCreateMutationError,
    formCategoryCreateMutationLoading,
    formTemplateCreateMutationLoading,
    formTemplateData,
    formTemplateError,
    formTemplateFirstLoading,
    formTemplateGraphQLErrors,
    formTemplateLoading,
    formTemplateUpdateMutationLoading,
    formTemplateVersionCreateMutationLoading,
    formTemplateVersionData,
    formTemplateVersionUpdateMutationLoading,
    onCancelForm,
    onFormCategoryCreate,
    onSubmitForm,
    onVersionFieldChangeCallBack,
    onViewHandler,
  } = useActionsResult;

  const filteredFormTemplateVersionData = useMemo(() => {
    if (!hideDocumentQuestions) return formTemplateVersionData;
    const questions = (formTemplateVersionData?.questions || []) as Values[];
    const filteredQuestions = questions.filter((question) => {
      const fields = (question?.fields || []) as { kind?: string }[];
      return fields.some(
        ({ kind }) => kind !== FormTemplateQuestionFieldsKindEnum.Document,
      );
    });
    return {
      ...formTemplateVersionData,
      questions: filteredQuestions,
    };
  }, [formTemplateVersionData, hideDocumentQuestions]);

  const selectedVersionOption = useSelectedVersionOption({
    formTemplateVersionData: filteredFormTemplateVersionData,
    t,
    versionId,
    viewMode,
  });

  const {
    getFormCategoriesLoadOptions,
    getDocumentationsLoadOptions,
    getTasksLoadOptions,
  } = useSearchListQueries({ id });

  const isEditOrDuplicate =
    viewMode === FormTemplateMode.Edit ||
    viewMode === FormTemplateMode.Duplicate;

  const { loading: documentationLinksPrefillLoading } =
    useDocumentationLinksPrefill({
      formRef,
      formTemplateId: isEditOrDuplicate ? id : undefined,
      initialDocumentationLinksRef,
      skip: isViewLocked,
    });

  const formFields = getFormTemplateFormFields({
    formTemplateId: id,
    getDocumentationsLoadOptions,
    getFormCategoriesLoadOptions,
    getTasksLoadOptions,
    isCategoriesViewOnly,
    isFullAdmin,
    isViewLocked,
    onVersionFieldChangeCallBack,
    openCategoryFormModal,
    openDocumentsModal,
    openTasksModal,
    selectedVersionOption,
    t,
    viewMode,
  });

  const breadCrumbsData = getBreadCrumbsDataByKey({
    entity: {
      versionId:
        formTemplateData.versionId ||
        _get(formTemplateData, ['currentVersion', 'id']) ||
        versionId,
      id: formTemplateData.id || id,
      name: formTemplateData.name,
    },
    pathname,
    t,
  });

  if (formTemplateFirstLoading || documentationLinksPrefillLoading) {
    return <CircleLoader />;
  }

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      <FormTemplateBuilderForm
        closeCategoryFormModal={closeCategoryFormModal}
        formCategoryCreateMutationError={formCategoryCreateMutationError}
        formCategoryCreateMutationLoading={formCategoryCreateMutationLoading}
        formFields={formFields}
        formRef={formRef}
        formTemplateVersionCreateMutationLoading={
          formTemplateVersionCreateMutationLoading
        }
        formTemplateVersionUpdateMutationLoading={
          formTemplateVersionUpdateMutationLoading
        }
        formTemplateCreateMutationLoading={formTemplateCreateMutationLoading}
        formTemplateData={formTemplateData}
        formTemplateVersionData={filteredFormTemplateVersionData}
        formTemplateError={formTemplateError}
        formTemplateGraphQLErrors={formTemplateGraphQLErrors}
        formTemplateLoading={formTemplateLoading}
        formTemplateUpdateMutationLoading={formTemplateUpdateMutationLoading}
        isCategoryFormModalOpened={isCategoryFormModalOpened}
        onCancelForm={onCancelForm}
        onFormCategoryCreate={onFormCategoryCreate}
        onSubmitForm={onSubmitForm}
        onViewHandler={onViewHandler}
        viewMode={viewMode}
      />
      <DocumentsModalTableMultiselect
        isOpen={isDocumentsModalOpen}
        onClose={onCloseDocumentsModal}
        onSelect={onDocumentsSelect}
        formRef={formRef}
        fieldName={FormTemplateFieldNames.DocumentationLinks}
      />
      <TasksTableMultiselect
        isOpen={useSelectModalsResult.isModalOpened(SELECT_TASKS_MODAL)}
        onClose={onCloseTasksModal}
        onSelect={onTasksSelect}
        formRef={formRef}
        fieldName={FormTemplateFieldNames.Tasks}
        scope={tasksScope}
        hiddenFilterFields={TASKS_HIDDEN_FILTER_FIELDS}
      />
      <FormTemplateModals
        useActionsResult={useActionsResult}
        useModalManagementResult={useModalManagementResult}
      />
    </>
  );
}
